import { createFileRoute } from "@tanstack/react-router";
import { useSimpleForm } from "@/hooks/use-simple-form";
import { Button } from "@/components/ui/button";
import {
  InputField,
  LightSelectField,
  TextAreaField,
} from "@/components/ui/form";
import useAppStore from "@/store";
import { useTranslate } from "@/hooks/use-translate";

import type { FinanceAccount } from "@/store/FinanceAccount";
import { useEffect, useState } from "react";
import { Store, Wallet, WarehouseIcon } from "lucide-react";
import type { Warehouse } from "@/store/Warehouse";
import SelectStockBatch from "@/components/stock-batch/select-stock-batch";
import type { StockBatch } from "@/store/StockBatch";
import { unPick } from "@/lib/utils";
import type { Shop } from "@/store/Shop";

export const Route = createFileRoute(
  "/(dasboard)/inventory/stock-transfers/create"
)({
  component: CreateStockTransferPage,
});

function CreateStockTransferPage() {
  const store = useAppStore();
  const [shops, setShops] = useState<Shop[]>([]);

  const [senderFinanceAccountItems, setSenderFinanceAccountItems] = useState<
    FinanceAccount[]
  >([]);
  const [receiverFinanceAccountItems, setReceiverFinanceAccountItems] =
    useState<FinanceAccount[]>([]);
  const [stock_batch, setStockBatch] = useState<StockBatch>();
  const [senderStockBatchItems, setSenderStockBatchItems] = useState<
    StockBatch[]
  >([]);
  const [receiverWarehouseItems, setReceiverWarehouseItems] = useState<
    Warehouse[]
  >([]);

  const form = useSimpleForm({
    sender_shop_id: "",
    sender_batch_id: "",
    receiver_shop_id: "",
    receiver_warehouse_id: "",
    sender_finance_account_id: "",
    receiver_finance_account_id: "",
    payment_date: "",
    reason: "",
    quantity: "",
  });
  const { t } = useTranslate();

  const submit = async () => {
    store.stockTransfer
      .create({
        user_id: store.auth.session?.user.id,
        shop_id: store.auth?.session?.shop?.id,
        ...unPick(form.values, ["sender_shop_id"]),
      })
      .then(() => {
        store.stockTransfer.fetch();
        window.history.back();
      })
      .catch(store.errors.catch);
  };

  useEffect(() => {
    if (store.auth.session) {
      store.warehouse
        .fetch({
          business_id: store.auth.session?.business.id,
        })
        .then((items) => {
          setReceiverWarehouseItems(items);
        });

      store.financeAccount
        .fetch({})
        .then((items) => setSenderFinanceAccountItems(items));

      store.stockBatch.fetch({}, { save: false }).then((items) => {
        setSenderStockBatchItems(items);
      });

      store.shop.fetch({}).then((shops) => {
        setShops(shops);
      });
    }
  }, [store.auth.session]);

  return (
    <section>
      <div className="mt-4 flex justify-between mb-4">
        <h2 className="text-xl font-semibold">{t("Create stock transfer")}</h2>
      </div>
      <div>{store.errors.render()}</div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="bg-white rounded-md border-gray-200 p-6">
          <h2 className="text-lg font-semibold">{t("Transfer from")}</h2>
          <div className="mt-4 space-y-4">
            <SelectStockBatch
              label="Inventory Item"
              stock_batch={stock_batch}
              items={senderStockBatchItems}
              onSelected={(stock_batch: StockBatch | undefined) => {
                setStockBatch(stock_batch);
                if (stock_batch)
                  form.setValue("sender_batch_id", stock_batch.id);
              }}
            />

            <div className="flex items-center gap-2">
              <InputField
                label={t("Quantity")}
                type="number"
                name="quantity"
                value={form.values.quantity}
                onChange={(e: any) => {
                  const value = parseFloat(e.target.value);
                  store.errors.reset();
                  if (value > parseFloat(stock_batch?.quantity as any)) {
                    store.errors.set(
                      "quantity",
                      `The available quantity is ${stock_batch?.quantity}`
                    );
                  } else {
                    form.setValue("quantity", e.target.value);
                  }
                }}
                error={store.errors.values.quantity}
              />
              <div className="ml-4">
                <p className="text-sm mb-1.5">{t("Cost")}</p>
                <div className="py-1.5 bg-gray-200 px-4 rounded-md">
                  {store.shop.formatAmount(
                    form.values.quantity * (stock_batch?.unit_price as number)
                  )}
                </div>
              </div>
            </div>

            <TextAreaField
              label={t("Reason (Optional)")}
              name="reason"
              value={form.values.reason}
              onChange={form.handleChange}
              error={store.errors.values.reason}
            />
            <LightSelectField
              label={t("Sending Treasury account")}
              name="sender_finance_account_id"
              icon={<Wallet className="w-4 h-4" />}
              value={form.values.sender_finance_account_id}
              onChange={form.handleChange}
              options={
                senderFinanceAccountItems.map((item: FinanceAccount) => ({
                  value: item.id,
                  label: `${item.name}`,
                })) || []
              }
              error={store.errors.values.sender_finance_account_id}
            />
          </div>
        </div>
        <div className="bg-white rounded-md border-gray-200 p-6">
          <h2 className="text-lg font-semibold">Transfer to</h2>
          <div className="mt-4 space-y-4">
            <LightSelectField
              label={t("Receiving shop")}
              icon={<Store className="w-4 h-4" />}
              name="receiver_shop_id"
              value={form.values.receiver_shop_id}
              options={
                shops?.map((item) => ({
                  label: item.name,
                  value: item.id,
                })) || []
              }
              onChange={(e: any) => {
                const value = e.target.value;
                store.financeAccount
                  .fetch({
                    shop_id: value,
                  })
                  .then((items) => setReceiverFinanceAccountItems(items));

                form.setValue("receiver_shop_id", value);
              }}
              error={store.errors.values.receiver_shop_id}
            />

            <LightSelectField
              label={t("Receiver warehouse")}
              name="receiver_warehouse_id"
              icon={<WarehouseIcon className="w-4 h-4" />}
              value={form.values.receiver_warehouse_id}
              onChange={form.handleChange}
              options={
                receiverWarehouseItems.map((item: Warehouse) => ({
                  value: item.id,
                  label: item.name,
                })) || []
              }
              error={store.errors.values.receiver_warehouse_id}
            />

            <LightSelectField
              label={t("Receiver Treasury account")}
              name="receiver_finance_account_id"
              icon={<Wallet className="w-4 h-4" />}
              value={form.values.receiver_finance_account_id}
              onChange={form.handleChange}
              options={
                receiverFinanceAccountItems.map((item: FinanceAccount) => ({
                  value: item.id,
                  label: `${item.name} - ${store.shop.formatAmount(
                    item.balance
                  )}`,
                })) || []
              }
              error={store.errors.values.receiver_finance_account_id}
            />

            <InputField
              label={t("Payment Date")}
              type="date"
              name="payment_date"
              value={form.values.payment_date}
              onChange={form.handleChange}
              error={store.errors.values.payment_date}
            />
          </div>
        </div>
      </div>
      <Button
        className="mt-6 px-8 py-2"
        onClick={submit}
        loading={store.stockTransfer.loading("create")}
      >
        {t("Submit the transfert")}
      </Button>
    </section>
  );
}
